import { ChangeEvent, FC, ReactElement } from "react"

import { useTranslation } from "react-i18next"

import { TitledCheckbox } from "./TitledCheckbox"

export type VerificationRole = "kyc" | "email" | "phone"

interface IProps {
  kyc: boolean
  emailVerified: boolean
  phoneVerified: boolean
  /**
   * The field name behind each checkbox differs per form (legacy `PromoCode.kyc` vs bonus-template
   * `kyc_required`), so the CALLER owns the binding: it receives the semantic role + the new value
   * and writes its own field. Keeps this component form-agnostic (Formik or plain state).
   */
  onToggle: (role: VerificationRole, checked: boolean) => void
  /** Email/phone gates are only meaningful for "free money" (no-deposit) bonuses — legacy parity. */
  showEmailPhone?: boolean
}

/**
 * Shared player-verification requirement checkboxes (KYC / email / phone). Rendered by both the
 * legacy promo modal and the Bonus V2 form so the UI + translations stay in one place; each form
 * maps the roles to its own field names via `onToggle`.
 */
export const VerificationCheckboxes: FC<IProps> = ({
  kyc,
  emailVerified,
  phoneVerified,
  onToggle,
  showEmailPhone = true,
}): ReactElement => {
  const { t } = useTranslation("translation", { useSuspense: false })

  return (
    <>
      <TitledCheckbox
        id="verification_kyc"
        name="verification_kyc"
        title={t("promo.kycRequired")}
        checked={kyc}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onToggle("kyc", e.target.checked)}
      />
      {showEmailPhone && (
        <>
          <TitledCheckbox
            id="verification_email"
            name="verification_email"
            title={t("promo.emailVerified")}
            checked={emailVerified}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onToggle("email", e.target.checked)}
          />
          <TitledCheckbox
            id="verification_phone"
            name="verification_phone"
            title={t("promo.phoneVerified")}
            checked={phoneVerified}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onToggle("phone", e.target.checked)}
          />
        </>
      )}
    </>
  )
}
